import {createContext, type PropsWithChildren, use, useCallback, useMemo, useState} from 'react'
import {useSearchParams} from '@github-ui/use-navigate'

type SearchType = 'apps' | 'actions'

interface SearchTypeContextType {
  type: SearchType | null
  setType: (type: string | null) => void
}

const SearchTypeContext = createContext<SearchTypeContextType | undefined>(undefined)

export function useSearchType() {
  const context = use(SearchTypeContext)
  if (!context) throw new Error('useSearchType must be used within a SearchTypeProvider')
  return context
}

export function SearchTypeProvider({children}: PropsWithChildren) {
  const [searchParams] = useSearchParams()
  const normalizeType = useCallback(
    (value: string | null): SearchType | null => (value === 'apps' || value === 'actions' ? value : null),
    [],
  )

  const [type, setTypeState] = useState<SearchType | null>(() => normalizeType(searchParams.get('type')))
  const setType = useCallback((value: string | null) => setTypeState(normalizeType(value)), [normalizeType])

  const value = useMemo(() => ({type, setType}), [type, setType])
  return <SearchTypeContext value={value}>{children}</SearchTypeContext>
}
